import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from '@amzn/storm-ui';
import { CustomCard } from '../../../UiComponentsLibrary/CustomCard';
import { CustomButton } from '../../../UiComponentsLibrary/CustomButton';
import { CustomConfirmationModal } from '../../../UiComponentsLibrary/CustomConfirmationModal';
import { CustomSnackbar } from '../../../UiComponentsLibrary/CustomSnackbar';
import { SnackbarVariants } from '../../../UiComponentsLibrary/CustomSnackbar';
import { useEditInventoryForm } from '../../hooks/useEditInventoryForm';
import { getDropdownFieldsConfig } from '../../constants/formConfig';
import { DropdownField } from './FormField';
import FormInputField from '../../../Shared/components/FormInputField';
import {
  FormContainer,
  ButtonRow,
  InputFieldWrapper,
  StyledFootNote,
} from './EditInventoryForm.styles';

export const EditInventoryForm = () => {
  const { t } = useTranslation();
  const { formFieldProps, submitProps, modalProps, snackbarProps } =
    useEditInventoryForm();

  const isFormIncomplete =
    !formFieldProps.selectedArtEvent ||
    !formFieldProps.selectedTypeOfPurchase ||
    !formFieldProps.selectedSlotPackage ||
    !formFieldProps.selectedSegment;

  const hasNewValue =
    !!formFieldProps.newCpm || !!formFieldProps.newTotalImpressions;

  const isSubmitDisabled =
    isFormIncomplete || !hasNewValue || submitProps.isSubmitting;

  const dropdownFields = useMemo(
    () => getDropdownFieldsConfig(t, formFieldProps),
    [t, formFieldProps]
  );

  const modalMessage = modalProps.validationData ? (
    <>
      <Text>
        {t('edit-inventory-modal-bookings-affected', {
          total: modalProps.validationData.bookingImpact.total,
        })}
      </Text>
      <Text>
        {t('edit-inventory-modal-bookings-misconfigured', {
          count: modalProps.validationData.bookingImpact.failedItems.length,
        })}
      </Text>
      <StyledFootNote>{t("edit-inventory-modal-foot-note")}</StyledFootNote>
    </>
  ) : (
    ''
  );

  const handleOldCpmChange = () => {
  };
  const handleOldTotalImpressionsChange = () => undefined;
  const handleArtEventChange = () => {
  };

  const snackbarConfig = snackbarProps.showSuccessSnackbar
    ? {
        variant: SnackbarVariants.SUCCESS,
        message: t('edit-inventory-success-message'),
        isVisible: true,
        onClose: () => snackbarProps.setShowSuccessSnackbar(false),
      }
    : snackbarProps.showErrorSnackbar
    ? {
        variant: SnackbarVariants.ERROR,
        message: t(snackbarProps.errorMessageKey),
        isVisible: true,
        onClose: () => snackbarProps.setShowErrorSnackbar(false),
      }
    : null;

  return (
    <FormContainer>
      <CustomCard id="edit-inventory-card" title={t('nav-edit-inventory')}>
        <InputFieldWrapper>
          <FormInputField
            id="art-event"
            label={t('edit-inventory-art-event-label')}
            value={formFieldProps.selectedArtEvent}
            onChange={handleArtEventChange}
            placeholder=""
            disabled
          />
        </InputFieldWrapper>

        {dropdownFields.map((field, index) => (
          <DropdownField key={index} {...field} />
        ))}

        <InputFieldWrapper>
          <FormInputField
            id="old-cpm"
            label={t('edit-inventory-old-cpm-label')}
            value={formFieldProps.oldCpm.toString()}
            onChange={handleOldCpmChange}
            placeholder=""
            disabled
          />
        </InputFieldWrapper>

        <InputFieldWrapper>
          <FormInputField
            id="new-cpm"
            label={t('edit-inventory-new-cpm-label')}
            value={formFieldProps.newCpm}
            onChange={(value: string) =>
              formFieldProps.handleNewCpmChange(value)
            }
            placeholder={t('edit-inventory-new-cpm-placeholder')}
            disabled={isFormIncomplete}
          />
        </InputFieldWrapper>

        <InputFieldWrapper>
          <FormInputField
            id="old-total-impressions"
            label={t('edit-inventory-old-total-impressions-label')}
            value={formFieldProps.oldTotalImpressions.toString()}
            onChange={handleOldTotalImpressionsChange}
            placeholder=""
            disabled
          />
        </InputFieldWrapper>

        <InputFieldWrapper>
          <FormInputField
            id="new-total-impressions"
            label={t('edit-inventory-new-total-impressions-label')}
            value={formFieldProps.newTotalImpressions}
            onChange={(value: string) =>
              formFieldProps.handleNewTotalImpressionsChange(value)
            }
            placeholder={t('edit-inventory-new-total-impressions-placeholder')}
            disabled={isFormIncomplete}
          />
        </InputFieldWrapper>

        <ButtonRow>
          <CustomButton
            text={submitProps.isSubmitting ? t('loading') : t('submit-button')}
            handleOnClickCb={submitProps.handleSubmit}
            disabled={isSubmitDisabled}
          />
        </ButtonRow>
      </CustomCard>

      <CustomConfirmationModal
        isOpen={modalProps.isModalOpen}
        title={t('edit-inventory-modal-title')}
        message={modalMessage}
        onConfirmCb={modalProps.handleModalConfirm}
        onCancelCb={modalProps.handleModalCancel}
        confirmButtonText={t('edit-inventory-confirm-button')}
      />

      {snackbarConfig && (
        <CustomSnackbar
          variant={snackbarConfig.variant}
          message={snackbarConfig.message}
          isVisible={snackbarConfig.isVisible}
          onClose={snackbarConfig.onClose}
        />
      )}
    </FormContainer>
  );
};
